import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast, Toaster } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CreditCard, Plus, Trash2, RefreshCw, CheckCircle2, XCircle, Filter, Layers, Printer } from "lucide-react";

export default function CardCodes() {
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({ card_status: "available", card_type: "", branch_id: "" });
  const [loading, setLoading] = useState(false);
  const [bulk, setBulk] = useState("");
  const [bulkType, setBulkType] = useState("physical");
  const [bulkBranch, setBulkBranch] = useState("");
  const [gen, setGen] = useState({ count: 50, prefix: "V", branch_id: "", format: "code128", start_number: "", pad: 8 });
  const [batches, setBatches] = useState([]);
  const [activeBatch, setActiveBatch] = useState("");
  const [layout, setLayout] = useState("labels");

  const loadBatches = async () => {
    try { const { data } = await api.get("/card-codes/batches"); setBatches(data || []); } catch { setBatches([]); }
  };
  const downloadPdf = async (params, name) => {
    try {
      const res = await api.get("/card-codes/print.pdf", { params: { ...params, layout }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(err?.response?.status === 404 ? "Nessun codice da stampare con questi filtri" : formatApiError(err)); }
  };
  const downloadExport = async (fmt, params, stem) => {
    try {
      const res = await api.get(`/card-codes/export.${fmt}`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `${stem}.${fmt}`; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(err?.response?.status === 404 ? "Nessun codice da esportare con questi filtri" : formatApiError(err)); }
  };
  const ExportButtons = ({ params, stem, size = "sm" }) => (
    <div className="inline-flex rounded-md overflow-hidden border border-zinc-800" data-testid={`${stem}-export`}>
      {["csv", "txt", "xlsx"].map((f) => (
        <button key={f} type="button" onClick={() => downloadExport(f, params, stem)} data-testid={`${stem}-export-${f}`}
          className={`px-2.5 ${size === "sm" ? "h-9 text-xs" : "h-8 text-[11px]"} font-mono uppercase text-zinc-300 hover:bg-zinc-800 hover:text-white border-r border-zinc-800 last:border-r-0`}>
          {f}
        </button>
      ))}
    </div>
  );
  const deleteBatch = async (b) => {
    if (!window.confirm(`Eliminare il lotto ${b.prefix || ""} ${b.first} → ${b.last}? Verranno rimossi i ${b.available} codici ancora disponibili; quelli già rilasciati restano.`)) return;
    try {
      const { data } = await api.delete(`/card-codes/batches/${b.id}`);
      toast.success(`Rimossi ${data.deleted} codici${data.kept_issued ? ` · ${data.kept_issued} rilasciati conservati` : ""}`);
      if (activeBatch === b.id) setActiveBatch("");
      load(); loadBatches();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const ean13Check = (d12) => { let s = 0; for (let i = 0; i < 12; i++) s += Number(d12[i]) * (i % 2 === 0 ? 1 : 3); return String((10 - (s % 10)) % 10); };
  const seqCode = (n) => {
    const p = (gen.prefix || "").toUpperCase();
    if (gen.format === "ean13") {
      if (!/^\d*$/.test(p) || p.length > 11) return null;
      const w = 12 - p.length; const num = String(n);
      if (num.length > w) return null;
      const b = p + num.padStart(w, "0"); return b + ean13Check(b);
    }
    return p + String(n).padStart(Number(gen.pad) || 1, "0");
  };
  const seqPreview = useMemo(() => {
    if (gen.start_number === "" || gen.start_number === null) return null;
    const s = Number(gen.start_number), c = Math.max(1, Number(gen.count) || 1);
    if (!Number.isInteger(s) || s < 0) return null;
    const first = seqCode(s), last = seqCode(s + c - 1);
    return { first, last, error: !first || !last };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gen]);
  const suggestNext = async () => {
    try {
      const { data } = await api.get("/card-codes/next-number", { params: { prefix: gen.prefix || "", format: gen.format } });
      setGen((g) => ({ ...g, start_number: data.next_number }));
      toast.success(data.existing ? `Nel pool ci sono ${data.existing} codici: si riparte da ${data.next_number}` : "Nessun codice con questo prefisso: si parte da 1");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.card_status) params.card_status = filters.card_status;
      if (filters.card_type) params.card_type = filters.card_type;
      if (filters.branch_id) params.branch_id = filters.branch_id;
      if (activeBatch) params.batch_id = activeBatch;
      const { data } = await api.get("/card-codes", { params });
      setRows(data || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => setBranches([]));
    load();
    loadBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBatch]);

  const submitBulk = async () => {
    const codes = bulk.split(/[\s,;\n]+/).map((s) => s.trim()).filter(Boolean);
    if (!codes.length) return toast.error("Nessun codice da inserire");
    try {
      const { data } = await api.post("/card-codes", { codes, type: bulkType, branch_id: bulkBranch || null });
      toast.success(`Caricati ${data.count} · ignorati ${data.duplicates.length}`);
      setBulk("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitGenerate = async () => {
    try {
      const { data } = await api.post("/card-codes/generate", {
        count: Number(gen.count) || 0,
        prefix: gen.prefix || "V",
        branch_id: gen.branch_id || null,
        format: gen.format || "code128",
        start_number: gen.start_number === "" ? null : Number(gen.start_number),
        pad: Number(gen.pad) || 8,
      });
      const range = data.first && data.first !== data.last ? ` · da ${data.first} a ${data.last}` : data.first ? ` · ${data.first}` : "";
      const skipped = data.skipped_existing?.length ? ` · ${data.skipped_existing.length} già esistenti saltati` : "";
      toast.success(`Generati ${data.count} codici virtuali (${data.format || "code128"})${range}${skipped}`);
      if (data.next_number != null) setGen((g) => ({ ...g, start_number: data.next_number }));
      load(); loadBatches();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeCode = async (id) => {
    if (!window.confirm("Rimuovere questo codice dal pool?")) return;
    try { await api.delete(`/card-codes/${id}`); toast.success("Rimosso"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const stats = useMemo(() => {
    const available = rows.filter((r) => r.status === "available").length;
    const issued = rows.filter((r) => r.status === "issued").length;
    return { available, issued, total: rows.length };
  }, [rows]);

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

  return (
    <div className="space-y-6 animate-fade-up" data-testid="cardcodes-page">
      <Toaster theme="light" position="top-right" richColors />
      <div>
        <span className="pill pill-gold"><CreditCard className="h-3 w-3" /> RILASCIO CARD</span>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Codici Carta</h1>
        <p className="text-zinc-400 mt-1">
          Carica codici delle carte fisiche pre-stampate o genera codici virtuali. Ogni codice sarà verificato in fase di rilascio.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-elev p-5 space-y-3" data-testid="cardcodes-bulk-card">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Plus className="h-4 w-4 text-amber-300" /> Carica codici (bulk)</h2>
          <Textarea data-testid="cardcodes-bulk-textarea" value={bulk} onChange={(e) => setBulk(e.target.value)}
            rows={5} placeholder="Incolla i codici separati da virgola, spazio o a capo. Es. W123456, W123457…"
            className="bg-zinc-950 border-zinc-800 font-mono text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-zinc-400 text-xs mb-1 inline-block">Tipo</Label>
              <Select value={bulkType} onValueChange={setBulkType}>
                <SelectTrigger data-testid="cardcodes-bulk-type" className="bg-zinc-950 border-zinc-800 h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="physical">Fisica</SelectItem>
                  <SelectItem value="virtual">Virtuale</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs mb-1 inline-block">Sede (opzionale)</Label>
              <Select value={bulkBranch || "__any__"} onValueChange={(v) => setBulkBranch(v === "__any__" ? "" : v)}>
                <SelectTrigger data-testid="cardcodes-bulk-branch" className="bg-zinc-950 border-zinc-800 h-9 text-sm"><SelectValue placeholder="Tutte" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Tutte le sedi (circuito)</SelectItem>
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button data-testid="cardcodes-bulk-submit" onClick={submitBulk}
            className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
            <Plus className="h-4 w-4 mr-1" /> Carica codici
          </Button>
        </div>

        <div className="card-elev p-5 space-y-3" data-testid="cardcodes-gen-card">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><RefreshCw className="h-4 w-4 text-emerald-300" /> Genera codici virtuali</h2>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-zinc-400 text-xs mb-1 inline-block">Quanti</Label>
              <Input type="number" min={1} max={1000} data-testid="cardcodes-gen-count"
                value={gen.count} onChange={(e) => setGen({ ...gen, count: e.target.value })}
                className="bg-zinc-950 border-zinc-800" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs mb-1 inline-block">Prefisso</Label>
              <Input maxLength={gen.format === "ean13" ? 11 : 6} data-testid="cardcodes-gen-prefix"
                value={gen.prefix} onChange={(e) => setGen({ ...gen, prefix: gen.format === "ean13" ? e.target.value.replace(/\D/g, "") : e.target.value.toUpperCase() })}
                placeholder={gen.format === "ean13" ? "es. 200 (solo cifre)" : "es. V"}
                className="bg-zinc-950 border-zinc-800 font-mono uppercase" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-zinc-400 text-xs mb-1 inline-block">Numero di partenza (progressivo)</Label>
              <div className="flex gap-1">
                <Input type="number" min={0} data-testid="cardcodes-gen-start"
                  value={gen.start_number} onChange={(e) => setGen({ ...gen, start_number: e.target.value })}
                  placeholder="vuoto = casuale" className="bg-zinc-950 border-zinc-800 font-mono" />
                <Button type="button" variant="outline" onClick={suggestNext} data-testid="cardcodes-gen-next" title="Prossimo numero libero nel pool" className="border-zinc-800 h-10 px-2 text-xs">Prossimo</Button>
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs mb-1 inline-block">{gen.format === "ean13" ? "Cifre numero" : "Cifre numero (zero a sinistra)"}</Label>
              <Input type="number" min={1} max={20} data-testid="cardcodes-gen-pad"
                value={gen.format === "ean13" ? 12 - (gen.prefix || "").length : gen.pad}
                disabled={gen.format === "ean13"}
                onChange={(e) => setGen({ ...gen, pad: e.target.value })}
                className="bg-zinc-950 border-zinc-800 font-mono disabled:opacity-40" />
            </div>
          </div>
          {seqPreview && (
            <div className={`rounded-lg border p-2 text-xs font-mono ${seqPreview.error ? "border-red-800/60 bg-red-500/10 text-red-300" : "border-zinc-800 bg-zinc-950 text-zinc-300"}`} data-testid="cardcodes-gen-preview">
              {seqPreview.error
                ? (gen.format === "ean13" ? "EAN-13 non valido: prefisso solo cifre (max 11) e numero che stia nelle cifre restanti" : "Anteprima non disponibile")
                : <>Anteprima: <span className="text-amber-300" data-testid="cardcodes-gen-preview-first">{seqPreview.first}</span>{seqPreview.last !== seqPreview.first && <> → <span className="text-amber-300" data-testid="cardcodes-gen-preview-last">{seqPreview.last}</span></>}
                  {gen.format === "ean13" && <span className="text-zinc-500"> · ultima cifra = check digit GS1</span>}</>}
            </div>
          )}
          <div>
            <Label className="text-zinc-400 text-xs mb-1 inline-block">Formato codice</Label>
            <Select value={gen.format} onValueChange={(v) => setGen({ ...gen, format: v, prefix: v === "ean13" ? (gen.prefix || "").replace(/\D/g, "") : gen.prefix })}>
              <SelectTrigger data-testid="cardcodes-gen-format" className="bg-zinc-950 border-zinc-800 h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ean13">EAN-13 (barcode 13 cifre, standard GS1)</SelectItem>
                <SelectItem value="code128">Code128 (alfanumerico, es. VIVI12345678)</SelectItem>
                <SelectItem value="qrcode">QR Code (identificativo esteso)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-zinc-500 mt-1">
              EAN-13: prefisso numerico + progressivo fino a 12 cifre, la 13ª è il check digit GS1 calcolato automaticamente (per uso interno GS1 consiglia i prefissi 20–29). Code128 è compatibile con qualsiasi scanner 1D. QR è ideale per Apple/Google Wallet e stampa mobile.
            </p>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs mb-1 inline-block">Sede (opzionale)</Label>
            <Select value={gen.branch_id || "__any__"} onValueChange={(v) => setGen({ ...gen, branch_id: v === "__any__" ? "" : v })}>
              <SelectTrigger data-testid="cardcodes-gen-branch" className="bg-zinc-950 border-zinc-800 h-9 text-sm"><SelectValue placeholder="Tutte" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">Tutte le sedi</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="cardcodes-gen-submit" onClick={submitGenerate}
            className="w-full bg-emerald-600 hover:bg-emerald-700">
            <RefreshCw className="h-4 w-4 mr-1" /> Genera
          </Button>
        </div>
      </div>

      <div className="card-elev p-5 space-y-3" data-testid="cardcodes-batches-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Layers className="h-4 w-4 text-sky-300" /> Lotti generati ({batches.length})</h2>
          <div className="flex items-center gap-2 text-xs">
            <Label className="text-zinc-400">Formato stampa</Label>
            <Select value={layout} onValueChange={setLayout}>
              <SelectTrigger data-testid="cardcodes-print-layout" className="bg-zinc-950 border-zinc-800 h-8 text-xs w-[240px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="labels">Etichette adesive A4 (3×8, 70×37 mm)</SelectItem>
                <SelectItem value="cards">Formato carta (85,6×54 mm, 2×5)</SelectItem>
                <SelectItem value="branded">Card con grafica del club (85,6×54 mm, 2×5 su A4)</SelectItem>
                <SelectItem value="print">Tipografia: 1 card per pagina, abbondanza 3 mm + crocini</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {batches.length === 0 && <div className="text-sm text-zinc-500 py-3" data-testid="cardcodes-batches-empty">Nessun lotto: ogni generazione di codici virtuali viene salvata qui come tiratura.</div>}
        <div className="space-y-2">
          {batches.map((b) => {
            const on = activeBatch === b.id;
            return (
              <div key={b.id} data-testid={`batch-${b.id}`}
                className={`rounded-xl border p-3 flex flex-wrap items-center gap-3 transition-colors ${on ? "border-sky-500 bg-sky-500/10" : "border-zinc-800 bg-zinc-950"}`}>
                <button type="button" onClick={() => setActiveBatch(on ? "" : b.id)} data-testid={`batch-select-${b.id}`} className="flex-1 min-w-[220px] text-left">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`pill ${b.format === "ean13" ? "pill-gold" : b.format === "qrcode" ? "pill-mint" : "pill-sky"} text-[10px]`}>{b.format === "ean13" ? "EAN-13" : b.format === "qrcode" ? "QR" : "Code128"}</span>
                    {b.prefix && <span className="font-mono text-xs text-zinc-400">prefisso {b.prefix}</span>}
                    <span className="font-mono text-sm text-amber-300">{b.first}{b.last !== b.first && <> → {b.last}</>}</span>
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    {new Date(b.created_at).toLocaleString("it-IT")} · {b.count} generati · <span className="text-emerald-300">{b.available} disponibili</span> · {b.issued} rilasciati{b.branch_name ? ` · ${b.branch_name}` : ""}{on ? " · filtro attivo sul pool" : ""}
                  </div>
                </button>
                <ExportButtons params={{ batch_id: b.id }} stem={`lotto-${b.first}-${b.last}`} size="xs" />
                <Button size="sm" variant="outline" onClick={() => downloadPdf({ batch_id: b.id }, `codici-carta-${b.first}-${b.last}.pdf`)} data-testid={`batch-print-${b.id}`}
                  className="border-zinc-800 h-9" disabled={b.remaining === 0}><Printer className="h-3.5 w-3.5 mr-1" /> Stampa PDF</Button>
                <Button size="sm" variant="outline" onClick={() => deleteBatch(b)} data-testid={`batch-delete-${b.id}`}
                  className="border-zinc-800 h-9 text-red-300 hover:text-red-200" disabled={b.available === 0}><Trash2 className="h-3.5 w-3.5 mr-1" /> Elimina lotto</Button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card-elev p-5 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Filter className="h-4 w-4 text-zinc-500" /> Pool codici ({stats.total}){activeBatch && <button type="button" onClick={() => setActiveBatch("")} data-testid="cardcodes-batch-clear" className="pill pill-mint text-[10px] ml-1">lotto selezionato ✕</button>}</h2>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-emerald-300 font-mono">{stats.available} disponibili</span>
            <span className="text-zinc-500 font-mono">{stats.issued} rilasciati</span>
            <ExportButtons params={{ ...(activeBatch ? { batch_id: activeBatch } : {}), card_status: filters.card_status || undefined, card_type: filters.card_type || undefined, branch_id: filters.branch_id || undefined }} stem="codici-carta" />
            <Button size="sm" variant="outline" disabled={rows.length === 0} data-testid="cardcodes-print-filtered"
              onClick={() => downloadPdf({ ...(activeBatch ? { batch_id: activeBatch } : {}), card_status: filters.card_status || undefined, card_type: filters.card_type || undefined, branch_id: filters.branch_id || undefined }, "codici-carta.pdf")}
              className="border-zinc-800 h-8"><Printer className="h-3.5 w-3.5 mr-1" /> Stampa selezione</Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <Select value={filters.card_status || "__any__"} onValueChange={(v) => setFilters({ ...filters, card_status: v === "__any__" ? "" : v })}>
            <SelectTrigger data-testid="cardcodes-filter-status" className="bg-zinc-950 border-zinc-800 h-9 text-sm"><SelectValue placeholder="Stato" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">Tutti gli stati</SelectItem>
              <SelectItem value="available">Disponibili</SelectItem>
              <SelectItem value="issued">Rilasciati</SelectItem>
              <SelectItem value="disabled">Disabilitati</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.card_type || "__any__"} onValueChange={(v) => setFilters({ ...filters, card_type: v === "__any__" ? "" : v })}>
            <SelectTrigger data-testid="cardcodes-filter-type" className="bg-zinc-950 border-zinc-800 h-9 text-sm"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">Fisiche e virtuali</SelectItem>
              <SelectItem value="physical">Fisiche</SelectItem>
              <SelectItem value="virtual">Virtuali</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.branch_id || "__any__"} onValueChange={(v) => setFilters({ ...filters, branch_id: v === "__any__" ? "" : v })}>
            <SelectTrigger data-testid="cardcodes-filter-branch" className="bg-zinc-950 border-zinc-800 h-9 text-sm"><SelectValue placeholder="Sede" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">Tutte le sedi</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} disabled={loading} className="border-zinc-800" data-testid="cardcodes-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Aggiorna
          </Button>
        </div>
        <div className="overflow-x-auto mobile-cards-wrap">
          <table className="w-full text-sm mobile-cards">
            <thead className="text-[10px] uppercase text-zinc-500 tracking-widest">
              <tr className="border-b border-zinc-900">
                <th className="text-left p-2">Codice</th>
                <th className="text-left p-2">Tipo</th>
                <th className="text-left p-2">Sede</th>
                <th className="text-left p-2">Stato</th>
                <th className="text-left p-2">Rilasciato</th>
                <th className="text-right p-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr className="mobile-cards-empty"><td colSpan={6} className="p-6 text-center text-zinc-500">Nessun codice.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-zinc-950" data-testid={`cardcode-${r.id}`}>
                  <td data-label="Codice" className="p-2 font-mono text-amber-300">{r.code}</td>
                  <td data-label="Tipo" className="p-2">
                    <span className={`pill ${r.type === "physical" ? "pill-gold" : "pill-mint"} text-[10px]`}>
                      {r.type === "physical" ? "Fisica" : "Virtuale"}
                    </span>
                  </td>
                  <td data-label="Sede" className="p-2 text-zinc-400">{r.branch_id ? branchName(r.branch_id) : "Tutte"}</td>
                  <td data-label="Stato" className="p-2">
                    {r.status === "available" && <span className="text-emerald-300 flex items-center gap-1 text-xs"><CheckCircle2 className="h-3 w-3" /> Disponibile</span>}
                    {r.status === "issued" && <span className="text-zinc-500 flex items-center gap-1 text-xs"><XCircle className="h-3 w-3" /> Rilasciato</span>}
                    {r.status === "disabled" && <span className="text-red-300 text-xs">Disabilitato</span>}
                  </td>
                  <td data-label="Rilasciato" className="p-2 text-zinc-500 text-xs">{r.issued_at ? new Date(r.issued_at).toLocaleString("it-IT") : "—"}</td>
                  <td data-label="" className="p-2 text-right">
                    {r.status !== "issued" && (
                      <button onClick={() => removeCode(r.id)}
                        data-testid={`cardcode-delete-${r.id}`}
                        className="p-1.5 rounded-md text-zinc-500 hover:text-red-300 hover:bg-red-500/10"
                        title="Rimuovi dal pool">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
